'use client';

import '@/components/frontend/object-audio/demo.css';
import React, { useContext, useEffect, useRef } from 'react';
import { Music4, Plus } from 'lucide-react';
import { LevelMapItem, LevelsControls, LevelsResults, LevelType, sizes } from './levels';
import { MoveableItem, PathItem } from './moveable-item';
import { cn } from '@/lib/shared/ui';
import { useStereoAudio } from '@/lib/frontend/hooks/useStereoAudio';
import { distanceBetween, Point } from '@/lib/frontend/utils/oa-demo';
import { AudioContext } from './object-audio-context';
import Image, { StaticImageData } from 'next/image';

type Props = {
  objectIcon?: React.ReactNode;
  src?: string;
  backgroundImage?: StaticImageData;
  initialLevels?: LevelType[];
  type?: DemoType;
  aspectRatio?: string;
  maxWidth?: string;
};

const objectClasses = `top-0 left-0 bg-accent text-background-color rounded-full flex items-center justify-center`;

export enum DemoType {
  Drag = 'drag',
  Path = 'path',
}

const levelIds = ['A', 'B', 'C', 'D', 'E'];

export const Demo = ({
  src = '/audio/synth.mp3',
  backgroundImage,
  initialLevels,
  aspectRatio = '1 / 1',
  maxWidth = '40rem',
  type = DemoType.Drag,
  objectIcon = <Music4 />,
}: Props) => {
  const constraintsRef = useRef<HTMLDivElement>(null);

  const objectAPointRef = useRef<Point | null>(null);

  const { togglePlay, pause, isPlaying, setLeftGain, setRightGain, initialized } = useStereoAudio({
    src,
  });

  const { audioSource, setAudioSource } = useContext(AudioContext);

  // Look for changes to the current audio source from provider and pause if it's different
  useEffect(() => {
    if (audioSource !== src) {
      pause();
    }
  }, [audioSource]);

  const [activeLevelId, setActiveLevelId] = React.useState<string | null>(null);
  const [computedLevels, setComputedLevels] = React.useState([0, 0]);
  const [containerDimensions, setContainerDimensions] = React.useState<{
    width: number;
    height: number;
  }>();

  const [levels, setLevels] = React.useState<LevelType[]>(initialLevels || []);

  const levelPositions = useRef<{ [key: string]: Point }>({});

  useEffect(() => {
    computeMix();
  }, [levels, containerDimensions]);

  useEffect(() => {
    setContainerDimensions({
      width: constraintsRef.current?.offsetWidth || 0,
      height: constraintsRef.current?.offsetHeight || 0,
    });
  }, []);

  let objectAStart: Point = {
    x: 0,
    y: 0,
  };

  if (containerDimensions) {
    objectAStart = {
      x: containerDimensions?.width / 2 - sizes.object / 2,
      y: containerDimensions?.height / 2 - sizes.object / 2,
    };
  }

  const getStart = (ratio: { x: number; y: number }) => {
    if (!containerDimensions) {
      return { x: 0, y: 0 };
    }

    return {
      x: containerDimensions.width * ratio.x - sizes.object / 2,
      y: containerDimensions.height * ratio.y - sizes.object / 2,
    };
  };

  const computeMix = () => {
    if (!initialized) {
      return;
    }
    const objectAPPoint = objectAPointRef.current || {
      x: objectAStart.x + sizes.object / 2,
      y: objectAStart.y + sizes.object / 2,
    };

    if (!containerDimensions) {
      return;
    }

    const computedWithDistances = levels.map((level) => {
      const levelStart = getStart(level.start);
      const position = levelPositions.current[level.id] || {
        x: levelStart.x + sizes.object / 2,
        y: levelStart.y + sizes.object / 2,
      };

      const distance = distanceBetween(position, {
        x: objectAPPoint.x,
        y: objectAPPoint.y,
      });

      return {
        level,
        distance: Math.max(distance, 0.0001),
      };
    });

    // Use inverse distance as weight
    const sumInverseDistances = computedWithDistances.reduce(
      (acc, result) => acc + 1 / result.distance,
      0,
    );

    const computedWithRatios = computedWithDistances.map((result) => {
      const ratio = 1 / result.distance / sumInverseDistances;
      const computedLevels = result.level.levels.map((l) => l.value * ratio);

      return {
        ...result,
        ratio,
        computedLevels,
      };
    });

    const leftLevel = computedWithRatios.reduce((sum, result) => {
      return sum + (result.computedLevels[0] || 0);
    }, 0);

    const rightLevel = computedWithRatios.reduce((sum, result) => {
      return sum + (result.computedLevels[1] || 0);
    }, 0);

    setLeftGain(leftLevel / 100);
    setRightGain(rightLevel / 100);

    setComputedLevels([leftLevel, rightLevel]);
  };

  const handleRemoveLevel = (id: string) => {
    setLevels((prev) => prev.filter((level) => level.id !== id));
    if (activeLevelId === id) {
      setActiveLevelId(null);
    }
  };

  const handleAddLevel = () => {
    const newLevel: LevelType = {
      id: levelIds[levels.length],
      label: `Levels ${levelIds[levels.length]}`,
      start: {
        x: 1 / 2,
        y: 1 / 2,
      },
      levels: [
        {
          label: 'L',
          value: 100,
        },
        {
          label: 'R',
          value: 100,
        },
      ],
    };

    setLevels((prev) => [...prev, newLevel]);
  };

  return (
    <div className="w-full">
      <div
        className="flex gap-2 w-full m-auto sm:flex-row flex-col-reverse"
        style={{
          maxWidth,
        }}
      >
        <div className="flex gap-2 justify-center flex-col w-full">
          <div
            ref={constraintsRef}
            className={cn(
              'bg-foreground/5 w-full rounded-lg border-2 border-foreground/10 relative',
              'background-grid',
            )}
            style={{
              aspectRatio,
            }}
          >
            {backgroundImage && (
              <Image
                height={800}
                width={1200}
                src={backgroundImage}
                alt="Background"
                className="absolute inset-0 w-full h-full object-cover opacity-30"
              />
            )}
            {containerDimensions && (
              <>
                {levels.map((l) => (
                  <MoveableItem
                    key={l.id}
                    initial={getStart(l.start)}
                    container={constraintsRef}
                    onTapStart={() => {
                      setActiveLevelId(l.id);
                    }}
                    onDrag={(point) => {
                      levelPositions.current[l.id] = point;

                      computeMix();
                    }}
                  >
                    <LevelMapItem isActive={activeLevelId === l.id} level={l} />
                  </MoveableItem>
                ))}

                {type === DemoType.Path && (
                  <PathItem
                    container={constraintsRef}
                    isMoving={isPlaying}
                    onMove={(point) => {
                      objectAPointRef.current = point;

                      setTimeout(() => {
                        computeMix();
                      }, 0);
                    }}
                  >
                    <div
                      className={cn(objectClasses, isPlaying && 'animate-music-pulse')}
                      style={{
                        width: `${sizes.object}px`,
                        height: `${sizes.object}px`,
                      }}
                    >
                      {objectIcon}
                    </div>
                  </PathItem>
                )}

                {type === DemoType.Drag && (
                  <MoveableItem
                    initial={objectAStart}
                    container={constraintsRef}
                    onDrag={(point) => {
                      objectAPointRef.current = point;

                      computeMix();
                    }}
                  >
                    <div
                      className={cn(objectClasses, isPlaying && 'animate-music-pulse')}
                      style={{
                        width: `${sizes.object}px`,
                        height: `${sizes.object}px`,
                      }}
                    >
                      {objectIcon}
                    </div>
                  </MoveableItem>
                )}
              </>
            )}
          </div>

          <div className="flex gap-5 justify-center h-[15rem] p-3 border border-foreground/10 rounded-lg">
            <div className="flex gap-3">
              {levels.map((s) => (
                <LevelsControls
                  id={s.id}
                  key={s.id}
                  levels={s.levels}
                  onClick={() => {
                    setActiveLevelId(s.id);
                  }}
                  onRemove={() => {
                    handleRemoveLevel(s.id);
                  }}
                  isActive={activeLevelId === s.id}
                  onValueChange={(index, newValue) => {
                    setLevels((prev) =>
                      prev.map((slider) =>
                        slider.id === s.id
                          ? {
                              ...slider,
                              levels: slider.levels.map((level, i) =>
                                i === index ? { ...level, value: newValue } : level,
                              ),
                            }
                          : slider,
                      ),
                    );
                  }}
                />
              ))}
              {levels.length < 4 && (
                <div
                  onClick={handleAddLevel}
                  className="group cursor-pointer hover:bg-foreground/10 px-3 h-full bg-foreground/5 flex items-center justify-center rounded-lg"
                >
                  <Plus className="opacity-60 group-hover:opacity-100" />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="h-[15rem]">
          <LevelsResults
            isPlaying={isPlaying}
            onPlayPause={() => {
              togglePlay();

              if (!isPlaying) {
                setAudioSource(src);
              }
            }}
            levels={[
              {
                label: 'L',
                value: Math.round(computedLevels[0]),
              },
              {
                label: 'R',
                value: Math.round(computedLevels[1]),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};
